import type { CompositeFilterDescriptor, DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { DatePicker, type DatePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import { DropDownList, type DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { GridColumn, GridNoRecords, type GridCellProps, type GridDataStateChangeEvent, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useListProductsQuery } from "../../api/admin/adminProductApi";
import { useListPromotionEventsQuery } from "../../api/admin/adminPromotionEventApi";
import { useListPromotionTypesQuery } from "../../api/admin/adminPromotionTypeApi";
import AppGrid from "../../components/grid/appGrid";
import { FundingSource, type CustomerDto, type ProductDto, type PromotionEventDto, type PromotionTypeDto, type RetailerDto } from "../../contracts";
import { formatDate, toDate } from "../../utils/dateValueConversion";
import { formatCurrency } from "../../utils/numberValueConversion";
import { useListAdminRetailersQuery } from "../../api/admin/adminRetailerApi";

const initialSort: SortDescriptor[] = [{ field: "fromDate", dir: "desc" }];
const selectorSort: SortDescriptor[] = [{ field: "name", dir: "asc" }];
const selectorRequest: DataSourceRequestState = { skip: 0, take: 1000, sort: selectorSort };

type FundingSourceOption = { id: FundingSource | 0; name: string };

const fundingSourceOptions: FundingSourceOption[] = [
    { id: FundingSource.Brand, name: "Brand" },
    { id: FundingSource.Retailer, name: "Retailer" },
    { id: FundingSource.Joint, name: "Joint" }
];

const fundingSourceLabels: Record<FundingSource, string> = {
    [FundingSource.Brand]: "Brand",
    [FundingSource.Retailer]: "Retailer",
    [FundingSource.Joint]: "Joint"
};

const buildFilter = (selectedCustomer: CustomerDto,
    selectedRetailer: RetailerDto | null,
    selectedProduct: ProductDto | null,
    selectedPromotionType: PromotionTypeDto | null,
    selectedFundingSource: FundingSourceOption | null,
    fromDate: Date | null,
    toDateValue: Date | null,
    dataStateFilter?: CompositeFilterDescriptor): CompositeFilterDescriptor => ({
    logic: "and",
    filters: [
        ...(dataStateFilter == null ? [] : [dataStateFilter]),
        { field: "customerId", operator: "eq", value: selectedCustomer.id },
        ...(selectedRetailer == null ? [] : [{ field: "retailerId", operator: "eq", value: selectedRetailer.id }]),
        ...(selectedProduct == null ? [] : [{ field: "productId", operator: "eq", value: selectedProduct.id }]),
        ...(selectedPromotionType == null ? [] : [{ field: "promotionTypeId", operator: "eq", value: selectedPromotionType.id }]),
        ...(selectedFundingSource == null ? [] : [{ field: "fundingSource", operator: "eq", value: selectedFundingSource.id }]),
        ...(fromDate == null ? [] : [{ field: "toDate", operator: "gte", value: fromDate }]),
        ...(toDateValue == null ? [] : [{ field: "fromDate", operator: "lte", value: toDateValue }])
    ]
});

const buildPromotionEventRequest = (dataState: DataSourceRequestState,
    selectedCustomer: CustomerDto | null,
    selectedRetailer: RetailerDto | null,
    selectedProduct: ProductDto | null,
    selectedPromotionType: PromotionTypeDto | null,
    selectedFundingSource: FundingSourceOption | null,
    fromDate: Date | null,
    toDateValue: Date | null): DataSourceRequestState | typeof skipToken => {
    if (selectedCustomer == null) {
        return skipToken;
    }

    return {
        ...dataState,
        filter: buildFilter(selectedCustomer, selectedRetailer, selectedProduct, selectedPromotionType, selectedFundingSource, fromDate, toDateValue, dataState.filter)
    };
};

const TextCell = (field: string): React.FC<GridCellProps> => props => {
    const value = (props.dataItem as Record<string, unknown>)[field];

    return <td className="k-table-td">{typeof value === "string" && value.trim() !== "" ? value : "-"}</td>;
};

const DateRangeCell: React.FC<GridCellProps> = props => {
    const promotionEvent = props.dataItem as PromotionEventDto;
    const fromDate = toDate(promotionEvent.from_date);
    const toDateValue = toDate(promotionEvent.to_date);

    return <td className="k-table-td">{fromDate == null && toDateValue == null ? "-" : `${fromDate == null ? "-" : formatDate(fromDate)} - ${toDateValue == null ? "-" : formatDate(toDateValue)}`}</td>;
};

const CurrencyCell = (field: keyof PromotionEventDto): React.FC<GridCellProps> => props => {
    const promotionEvent = props.dataItem as PromotionEventDto;
    const value = promotionEvent[field];

    return <td className="k-table-td text-end">{typeof value === "number" ? formatCurrency(value) : "-"}</td>;
};

const PercentCell: React.FC<GridCellProps> = props => {
    const promotionEvent = props.dataItem as PromotionEventDto;

    return <td className="k-table-td text-end">{promotionEvent.discount_depth_percent == null ? "-" : `${promotionEvent.discount_depth_percent}%`}</td>;
};

const FundingSourceCell: React.FC<GridCellProps> = props => {
    const promotionEvent = props.dataItem as PromotionEventDto;

    return <td className="k-table-td">{promotionEvent.funding_source == null ? "-" : fundingSourceLabels[promotionEvent.funding_source]}</td>;
};

const PromotionEventManagement: React.FC = () => {
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [selectedRetailer, setSelectedRetailer] = useState<RetailerDto | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(null);
    const [selectedPromotionType, setSelectedPromotionType] = useState<PromotionTypeDto | null>(null);
    const [selectedFundingSource, setSelectedFundingSource] = useState<FundingSourceOption | null>(null);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDateValue, setToDateValue] = useState<Date | null>(null);
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 50, sort: initialSort });

    const promotionEventRequest = buildPromotionEventRequest(dataState, selectedCustomer, selectedRetailer, selectedProduct, selectedPromotionType, selectedFundingSource, fromDate, toDateValue);
    const retailerRequest: DataSourceRequestState | typeof skipToken = selectedCustomer == null ? skipToken : { ...selectorRequest, filter: { logic: "and", filters: [{ field: "customerId", operator: "eq", value: selectedCustomer.id }] } };
    const productRequest: DataSourceRequestState | typeof skipToken = selectedCustomer == null ? skipToken : { ...selectorRequest, filter: { logic: "and", filters: [{ field: "retailer.customerId", operator: "eq", value: selectedCustomer.id }, ...(selectedRetailer == null ? [] : [{ field: "retailerId", operator: "eq", value: selectedRetailer.id }])] } };
    const promotionTypeRequest: DataSourceRequestState | typeof skipToken = selectedCustomer == null ? skipToken : { ...selectorRequest, filter: { logic: "and", filters: [{ field: "retailer.customerId", operator: "eq", value: selectedCustomer.id }, ...(selectedRetailer == null ? [] : [{ field: "retailerId", operator: "eq", value: selectedRetailer.id }])] } };

    const { data, isFetching, isError } = useListPromotionEventsQuery(promotionEventRequest);
    const { data: customerData, isFetching: isCustomersFetching, isError: isCustomersError } = useListCustomersQuery(selectorRequest);
    const { currentData: retailerData, isFetching: isRetailersFetching, isError: isRetailersError } = useListAdminRetailersQuery(retailerRequest);
    const { currentData: productData, isFetching: isProductsFetching, isError: isProductsError } = useListProductsQuery(productRequest);
    const { currentData: promotionTypeData, isFetching: isPromotionTypesFetching, isError: isPromotionTypesError } = useListPromotionTypesQuery(promotionTypeRequest);

    const resetPaging = (): void => setDataState(previous => ({ ...previous, skip: 0 }));
    const handleCustomerChange = (event: DropDownListChangeEvent): void => {
        const customer = event.value as CustomerDto | null;
        setSelectedCustomer(customer?.id === 0 ? null : customer);
        setSelectedRetailer(null);
        setSelectedProduct(null);
        setSelectedPromotionType(null);
        resetPaging();
    };
    const handleRetailerChange = (event: DropDownListChangeEvent): void => {
        const retailer = event.value as RetailerDto | null;
        setSelectedRetailer(retailer?.id === 0 ? null : retailer);
        setSelectedProduct(null);
        setSelectedPromotionType(null);
        resetPaging();
    };

    const emptyMessage = selectedCustomer == null ? "Select a customer to view promotion events." : isError ? "Unable to load promotion events. Please try again." : "No promotion events found for the selected filters.";

    return (
        <div className="d-flex flex-column gap-3 p-3 h-100 min-h-0 overflow-hidden">
            <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-promotion-event-customer">Customer</label>
                    <DropDownList id="admin-promotion-event-customer" data={customerData?.data ?? []} textField="name" dataItemKey="id" value={selectedCustomer} onChange={handleCustomerChange} disabled={isCustomersFetching} defaultItem={{ id: 0, name: isCustomersFetching ? "Loading customers..." : "Select customer" }} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-promotion-event-retailer">Retailer</label>
                    <DropDownList id="admin-promotion-event-retailer" data={retailerData?.data ?? []} textField="name" dataItemKey="id" value={selectedRetailer} onChange={handleRetailerChange} disabled={selectedCustomer == null || isRetailersFetching} defaultItem={{ id: 0, name: isRetailersFetching ? "Loading retailers..." : "All retailers" }} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-promotion-event-product">Product</label>
                    <DropDownList
                        id="admin-promotion-event-product"
                        data={productData?.data ?? []}
                        textField="name"
                        dataItemKey="id"
                        value={selectedProduct}
                        onChange={event => {
                            const product = event.value as ProductDto | null;
                            setSelectedProduct(product?.id === 0 ? null : product);
                            resetPaging();
                        }}
                        disabled={selectedCustomer == null || isProductsFetching}
                        defaultItem={{ id: 0, name: isProductsFetching ? "Loading products..." : "All products" }}
                    />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-promotion-event-type">Promotion type</label>
                    <DropDownList
                        id="admin-promotion-event-type"
                        data={promotionTypeData?.data ?? []}
                        textField="name"
                        dataItemKey="id"
                        value={selectedPromotionType}
                        onChange={event => {
                            const promotionType = event.value as PromotionTypeDto | null;
                            setSelectedPromotionType(promotionType?.id === 0 ? null : promotionType);
                            resetPaging();
                        }}
                        disabled={selectedCustomer == null || isPromotionTypesFetching}
                        defaultItem={{ id: 0, name: isPromotionTypesFetching ? "Loading promotion types..." : "All promotion types" }}
                    />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-promotion-event-funding-source">Funding source</label>
                    <DropDownList
                        id="admin-promotion-event-funding-source"
                        data={fundingSourceOptions}
                        textField="name"
                        dataItemKey="id"
                        value={selectedFundingSource}
                        onChange={event => {
                            const fundingSource = event.value as FundingSourceOption | null;
                            setSelectedFundingSource(fundingSource?.id === 0 ? null : fundingSource);
                            resetPaging();
                        }}
                        defaultItem={{ id: 0, name: "All funding sources" }}
                    />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-promotion-event-from-date">From date</label>
                    <DatePicker
                        id="admin-promotion-event-from-date"
                        value={fromDate}
                        onChange={(event: DatePickerChangeEvent) => {
                            setFromDate(event.value);
                            resetPaging();
                        }}
                    />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-promotion-event-to-date">To date</label>
                    <DatePicker
                        id="admin-promotion-event-to-date"
                        value={toDateValue}
                        onChange={(event: DatePickerChangeEvent) => {
                            setToDateValue(event.value);
                            resetPaging();
                        }}
                    />
                </div>
            </div>
            {(isError || isCustomersError || isRetailersError || isProductsError || isPromotionTypesError) && <div className="alert alert-danger mb-0" role="alert">Unable to load promotion events, promotion types, products, retailers, or customers. Please try again.</div>}
            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    scrollable="scrollable"
                    showLoader={isFetching || isCustomersFetching || isRetailersFetching || isProductsFetching || isPromotionTypesFetching}
                    dataItemKey="id"
                    sortable={false}
                    filterable={false}
                    data={data?.data ?? []}
                    resizable={true}
                    initialState={dataState}
                    onPageChange={(event: GridPageChangeEvent) => setDataState(previous => ({ ...previous, skip: event.page.skip, take: event.page.take }))}
                    onDataStateChange={(event: GridDataStateChangeEvent) => setDataState({ ...event.dataState, skip: event.dataState.skip, take: event.dataState.take })}
                    sort={dataState.sort}
                    total={data?.total ?? 0}
                    skip={dataState.skip}
                    take={dataState.take}
                    defaultSort={initialSort}
                    onSortChange={(event: GridSortChangeEvent) => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }))}
                    pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                    style={{ height: "100%" }}
                >
                    <GridNoRecords>{!isFetching && <span className="text-muted">{emptyMessage}</span>}</GridNoRecords>
                    <GridColumn field="customerName" title="Customer" width="180px" cells={{ data: TextCell("customer_name") }} />
                    <GridColumn field="retailerName" title="Retailer" width="180px" cells={{ data: TextCell("retailer_name") }} />
                    <GridColumn field="productName" title="Product" width="260px" cells={{ data: TextCell("product_name") }} />
                    <GridColumn field="promotionTypeName" title="Promotion Type" width="180px" cells={{ data: TextCell("promotion_type_name") }} />
                    <GridColumn field="discountDepthPercent" title="Discount Depth" width="150px" cells={{ data: PercentCell }} />
                    <GridColumn field="promoPrice" title="Promo Price" width="140px" cells={{ data: CurrencyCell("promo_price") }} />
                    <GridColumn field="tradeSpend" title="Trade Spend" width="150px" cells={{ data: CurrencyCell("trade_spend") }} />
                    <GridColumn field="fundingSource" title="Funding Source" width="160px" cells={{ data: FundingSourceCell }} />
                    <GridColumn field="fromDate" title="Date Range" width="260px" cells={{ data: DateRangeCell }} />
                    <GridColumn field="description" title="Description" width="320px" cells={{ data: TextCell("description") }} />
                </AppGrid>
            </div>
        </div>
    );
};

export default PromotionEventManagement;
